import { ConfirmDialog } from '@/components/confirm-dialog';
import { matricesApi } from '@/lib/api';
import { matrixTitle } from '@/lib/matrix-grid-filters';
import type { MatrixSummary } from '@mss_creator/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const DELETE_MATRIX_ERROR_MESSAGE = 'La suppression a échoué. Réessayez.';

export interface DeleteMatrixDialogProps {
  /** The matrix to delete; `null` keeps the dialog closed. */
  matrix: Pick<MatrixSummary, 'id' | 'sujet'> | null;
  companyId: string;
  onClose: () => void;
}

/**
 * Confirmation before deleting a matrix, in any state (issue #16, planche
 * 05) — replaces the former `window.confirm`. The focus lands on « Annuler »
 * so Enter never deletes by accident, and Escape closes. While the call runs
 * « Supprimer » reads « Suppression… »; on failure the dialog stays open with
 * the error under the text, and « Supprimer » retries.
 */
export function DeleteMatrixDialog({ matrix, companyId, onClose }: DeleteMatrixDialogProps) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => matricesApi.delete(id),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<MatrixSummary[]>(['matrices', companyId], (previous) =>
        previous?.filter((entry) => entry.id !== id),
      );
      void queryClient.invalidateQueries({ queryKey: ['matrices', companyId] });
      // The folder counters follow the matrices.
      void queryClient.invalidateQueries({ queryKey: ['folders', companyId] });
      onClose();
    },
  });

  function handleCancel() {
    if (mutation.isPending) return;
    mutation.reset();
    onClose();
  }

  return (
    <ConfirmDialog
      open={matrix !== null}
      title={matrix ? `Supprimer « ${matrixTitle(matrix)} » ?` : ''}
      description="Les cellules, les dimensions, les documents de contexte et tout l'historique de cette matrice seront supprimés. Cette action est irréversible."
      confirmLabel="Supprimer"
      pendingLabel="Suppression…"
      confirmIcon="delete"
      confirmVariant="destructive"
      isPending={mutation.isPending}
      errorMessage={mutation.isError ? DELETE_MATRIX_ERROR_MESSAGE : null}
      onConfirm={() => matrix && mutation.mutate(matrix.id)}
      onCancel={handleCancel}
    />
  );
}
